
import { addressTitle } from '@shared/listing';
import { Button, Card, Container, Eyebrow, Heading, Row, Spacer, Stack, Text } from '@client/shared/design';
import { Stepper } from './Stepper.tsx';
import { StepBasics } from './StepBasics.tsx';
import { StepAvailability } from './StepAvailability.tsx';
import { StepAccess, type AgentOption } from './StepAccess.tsx';
import { useNewListingForm } from './use-new-listing-form.ts';
import { useGroupAgents } from './use-group-agents.ts';
import type { CreatedListing } from './new-listing-mutation.ts';
import { CreatedSummary } from './CreatedSummary.tsx';
import styles from './NewListing.module.css';

export interface NewListingProps {
  agents?: readonly AgentOption[];
  onCreated?: (listing: CreatedListing) => void;
  onCancel?: () => void;
}

export function NewListing({ agents: agentsProp, onCreated, onCancel }: NewListingProps) {
  const loaded = useGroupAgents();
  const agents = agentsProp ?? loaded.agents;
  const wizard = useNewListingForm(onCreated);
  const { form, step, stepIndex, isFirstStep, isLastStep, next, back, goTo, stepError, submitError, submitting, created } =
    wizard;

  if (created) {
    return (
      <Container size="default">
        <div className={styles.page}>
          <CreatedSummary listing={created} onDone={onCancel} />
        </div>
      </Container>
    );
  }

  return (
    <Container size="default">
      <div className={styles.page}>
        <Stack gap={2} className={styles.head}>
          <Eyebrow>New listing</Eyebrow>
          <Heading level={1} className={styles.headTitle}>
            {isFirstStep ? 'Make a property available' : addressTitle(form.state.values.address)}
          </Heading>
          <Text tone="soft" className={styles.headIntro}>
            Three quick steps and agents can start booking open houses. You can edit any of this
            later.
          </Text>
        </Stack>

        <div className={styles.layout}>
          <aside className={styles.rail}>
            <Stepper activeIndex={stepIndex} onJump={goTo} />
          </aside>

          <Card pad="lg" spine className={styles.panel} as="section" aria-live="polite">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (isLastStep) void form.handleSubmit();
                else next();
              }}
              noValidate
            >
              <Stack gap={7}>
                <div className={styles.stepPanel} data-step={step}>
                  {step === 'basics' && <StepBasics form={form} />}
                  {step === 'availability' && <StepAvailability form={form} />}
                  {step === 'access' && <StepAccess form={form} agents={agents} />}
                </div>

                {(stepError || submitError) && (
                  <p className={styles.formError} role="alert">
                    <ErrorMark />
                    {submitError ?? stepError}
                  </p>
                )}

                <Row gap={3} align="center" className={styles.footer}>
                  {isFirstStep ? (
                    onCancel && (
                      <Button type="button" variant="ghost" onClick={onCancel}>
                        Cancel
                      </Button>
                    )
                  ) : (
                    <Button type="button" variant="secondary" onClick={back} disabled={submitting}>
                      Back
                    </Button>
                  )}
                  <Spacer />
                  <Text size="xs" tone="muted" className={styles.stepCount}>
                    Step {stepIndex + 1} of 3
                  </Text>
                  {isLastStep ? (
                    <Button type="submit" variant="primary" loading={submitting}>
                      Make available
                    </Button>
                  ) : (
                    <Button type="submit" variant="primary">
                      Continue
                    </Button>
                  )}
                </Row>
              </Stack>
            </form>
          </Card>
        </div>
      </div>
    </Container>
  );
}

function ErrorMark() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true" className={styles.errorMark}>
      <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.4" />
      <path d="M8 4.5v4.2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <circle cx="8" cy="11.4" r="0.85" fill="currentColor" />
    </svg>
  );
}

export default NewListing;
